import { useState, type ReactNode } from 'react';
import type { InboundEmail } from '@erp/rpc';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DocumentViewer } from '@/components/common/DocumentViewer';
import { SmartEmailViewer } from './SmartEmailViewer';
import { inboundEmailToEmailData } from './utils';

/**
 * One source behind the record being viewed: a labeled tab in the panel. Most
 * are emails; a source that never arrived by email (a portal download, a bank
 * memo) is shown as its document.
 */
export type EmailTab = { key: string; label: ReactNode } & (
  | { email: InboundEmail; documentId?: never }
  | { documentId: string; email?: never }
);

function emailTabSourceId(tab: EmailTab): string {
  return tab.email ? tab.email.id : tab.documentId;
}

function EmailTabViewer({ tab }: { tab: EmailTab }) {
  if (!tab.email) {
    return <DocumentViewer documentId={tab.documentId} className="h-full" />;
  }
  const { email } = tab;
  return (
    <SmartEmailViewer
      emailData={inboundEmailToEmailData(email)}
      conversationId={email.conversationId}
      defaultTab="email"
      sourceEmailId={email.id}
      highlightedEmailIds={[email.id]}
    />
  );
}

function PersistentEmailTab({
  tab,
  active,
}: {
  tab: EmailTab;
  active: boolean;
}) {
  const [visited, setVisited] = useState(active);
  if (active && !visited) setVisited(true);

  return (
    <TabsContent
      value={tab.key}
      forceMount
      // Keep the same dimensions while hidden: display:none makes PDF viewers
      // measure zero width and redraw when the tab becomes visible again.
      className="absolute inset-0 mt-0 min-h-0 min-w-0 data-[hidden]:invisible"
    >
      {visited && <EmailTabViewer tab={tab} />}
    </TabsContent>
  );
}

/**
 * The right-hand email panel shared by the AR detail slide-ins (payments,
 * cash receipts): one tab per related email or document, no tab strip when there's only
 * one. Callers build the tabs — labels are domain-specific — and should skip
 * rendering the panel entirely when there are no tabs.
 */
export function EmailTabsPanel({ tabs }: { tabs: EmailTab[] }) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [firstTab] = tabs;
  const activeTab = tabs.find((tab) => tab.key === selectedKey) ?? firstTab;
  const activeKey = activeTab?.key ?? null;
  // Persist the fallback so a removed tab cannot reclaim selection if it returns.
  if (selectedKey !== activeKey) setSelectedKey(activeKey);
  if (!activeTab) return null;

  return (
    <Tabs
      value={activeTab.key}
      onValueChange={setSelectedKey}
      className="flex h-full min-h-0 flex-col"
    >
      {tabs.length > 1 && (
        <TabsList className="w-full shrink-0">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      )}
      <div className="relative flex-1 min-h-0 min-w-0">
        {tabs.map((tab) => (
          <PersistentEmailTab
            key={`${tab.key}:${emailTabSourceId(tab)}`}
            tab={tab}
            active={tab.key === activeTab.key}
          />
        ))}
      </div>
    </Tabs>
  );
}
